import { z } from 'zod';
import { passwordSchema } from './auth.js';

// Exportes : l'edition d'un element existant (edition.ts) doit accepter EXACTEMENT les
// memes formats que sa creation. Deux regex jumelles divergeraient en silence.
export const dateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date attendue au format AAAA-MM-JJ');
export const timeSchema = z.string().regex(/^\d{2}:\d{2}$/, 'Heure attendue au format HH:MM');

// ---------------------------------------------------------------------------
// DEUX NIVEAUX D'EXIGENCE, et la difference est voulue (audit du 2026-09-27).
//
// `dateSchema` et `timeSchema` ne jugent que la FORME. Ils servent a ce que le MODELE
// rend : les resserrer ferait rejeter toute une reponse, facturee, pour une seule date
// fausse — le piege deja paye les 28 et 29 aout 2026.
//
// `dateReelleSchema` et `heureReelleSchema` jugent aussi l'EXISTENCE. Ils servent a ce
// qu'un HUMAIN saisit : la, un refus clair vaut mieux qu'un 31 fevrier range en silence
// au 3 mars.
// ---------------------------------------------------------------------------

/**
 * Le jour ecrit existe-t-il ? `new Date('2026-02-31')` ne refuse rien : il deborde sur le
 * 3 mars. On verifie donc que la date RELUE est exactement celle qui a ete ecrite.
 */
export function jourExiste(jour: string): boolean {
  const date = new Date(`${jour}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === jour;
}

/** L'heure ecrite existe-t-elle ? De 00:00 a 23:59 : « 24:00 » est deja le lendemain. */
export function heureExiste(heure: string): boolean {
  const [heures, minutes] = heure.split(':').map(Number);
  return (
    heures !== undefined &&
    minutes !== undefined &&
    Number.isInteger(heures) &&
    Number.isInteger(minutes) &&
    heures >= 0 &&
    heures <= 23 &&
    minutes >= 0 &&
    minutes <= 59
  );
}

export const MESSAGE_FIN_AVANT_DEBUT = 'La fin doit venir après le début';

export const dateReelleSchema = dateSchema.refine(jourExiste, {
  message: "Cette date n'existe pas",
});

export const heureReelleSchema = timeSchema.refine(heureExiste, {
  message: "Cette heure n'existe pas",
});

/**
 * Une fin a-t-elle un sens apres ce debut ? Les deux heures sont au format HH:MM, sur le
 * MEME jour : la comparaison de chaines suffit. Une fin egale au debut ne dure rien, et
 * une fin sur une journee entiere (debut `null`) ne veut rien dire — un evenement ne
 * sait pas encore passer minuit.
 */
export function finApresDebut(debut: string | null, fin: string | null): boolean {
  if (fin === null) return true;
  if (debut === null) return false;
  return fin > debut;
}

/**
 * Une habitude qui se repete : « tous les lundis », « chaque matin ». freq WEEKLY avec
 * days = jours de semaine (0=dimanche..6=samedi) ; date de l'evenement = la PREMIERE
 * occurrence. `default(null)` : les fiches d'avant la recurrence restent valides.
 */
export const recurrencePayloadSchema = z
  .object({
    freq: z.enum(['DAILY', 'WEEKLY']),
    days: z.array(z.number().int().min(0).max(6)).max(7).default([]),
  })
  .nullable()
  .default(null);

/**
 * Les deux creneaux de table du foyer, tels que la base les connait (`enum MealSlot`).
 * Ecrire ici une valeur qui n'y figure pas ferait echouer l'ecriture, pas la validation.
 */
export const mealSlotSchema = z.enum(['LUNCH', 'DINNER']);

export const eventPayloadSchema = z.object({
  title: z.string().min(1).max(200),
  date: dateSchema,
  time: timeSchema.nullable(),
  endTime: timeSchema.nullable(),
  location: z.string().max(200).nullable(),
  needsConfirmation: z.boolean(),
  /**
   * Un REPAS : le meme evenement, pose sur un creneau de table. C'est ce qui le fait
   * apparaitre dans l'ecran Repas en plus du planning, sans double saisie.
   *
   * `default(null)` : un evenement ordinaire n'est pas un repas, les fiches d'avant
   * restent valides, et un modele qui omet le champ ne fait pas echouer la fiche.
   */
  mealSlot: mealSlotSchema.nullable().default(null),
  /**
   * D'AUTRES titres possibles pour la meme case : « Yassa poulet, ou Mafe, ou Riz
   * sauce arachide ». L'ecran les propose, on en choisit un, et c'est lui qui est
   * ecrit — le chemin de correction existant suffit, aucune route nouvelle.
   *
   * Plafonne a 3 : le but est d'ALLEGER la decision. Huit propositions par soir la
   * rendraient plus lourde qu'une page blanche.
   *
   * `default([])` : une proposition sans alternative se valide comme avant.
   */
  alternatives: z.array(z.string().min(1).max(200)).max(3).default([]),
  /**
   * Comment on s'y rend : « RER B », « TER Amiens-Paris », « voiture ».
   *
   * Texte LIBRE a dessein. On ne sait pas encore quelle forme la surveillance des
   * perturbations exigera : inventer une structure maintenant serait une supposition,
   * et il faudrait migrer deux fois.
   *
   * C'est l'UTILISATEUR qui le dit, jamais Colette qui le devine (arbitrage du porteur,
   * 2026-08-29) : une alerte sur un train qu'on ne prend pas ferait ignorer toutes les
   * suivantes, et ce jour-la la fonction aurait tout perdu.
   */
  transport: z.string().max(80).nullable().default(null),
  recurrence: recurrencePayloadSchema,
  /**
   * Une ROUTINE : une chose qui revient et que personne ne risque d'oublier (repas,
   * coucher, trajets quotidiens). Elle occupe le temps, mais ne part pas dans Google et
   * ne notifie pas — voir la spec des routines.
   *
   * `default(false)` : les fiches d'avant restent valides, et un modele qui omet le
   * champ ne fait pas echouer toute la comprehension.
   */
  estUneRoutine: z.boolean().default(false),
});

export const taskPayloadSchema = z.object({
  title: z.string().min(1).max(200),
  dueDate: dateSchema.nullable(),
  needsConfirmation: z.boolean(),
});

export const reminderPayloadSchema = z.object({
  message: z.string().min(1).max(300),
  date: dateSchema,
  time: timeSchema,
});

// ---------------------------------------------------------------------------
// Ce qu'un HUMAIN envoie en CORRIGEANT une proposition (audit du 2026-09-27).
//
// La meme forme que ce que le modele rend, jugee strictement : une correction « 31
// fevrier, 15:00 -> 10:00 » etait acceptee, et l'evenement ecrit le 3 mars avec une fin
// avant son debut. Le schema du modele, lui, ne change pas.
// ---------------------------------------------------------------------------

export const evenementCorrigeSchema = eventPayloadSchema
  .extend({
    date: dateReelleSchema,
    time: heureReelleSchema.nullable(),
    endTime: heureReelleSchema.nullable(),
  })
  .refine((evenement) => finApresDebut(evenement.time, evenement.endTime), {
    message: MESSAGE_FIN_AVANT_DEBUT,
    path: ['endTime'],
  });

export const tacheCorrigeeSchema = taskPayloadSchema.extend({
  dueDate: dateReelleSchema.nullable(),
});

export const rappelCorrigeSchema = reminderPayloadSchema.extend({
  date: dateReelleSchema,
  time: heureReelleSchema,
});

export const checklistKindSchema = z.enum(['COURSES', 'GENERALE']);

export const checklistPayloadSchema = z.object({
  title: z.string().min(1).max(200),
  items: z.array(z.string().min(1).max(120)).min(1).max(30),
  eventTitle: z.string().max(200).nullable(),
  // `default(GENERALE)` : les fiches d'avant le champ restent valides, et une liste dont
  // la nature est inconnue ne doit JAMAIS atterrir dans l'ecran Repas (bug du 2026-08-27).
  kind: checklistKindSchema.default('GENERALE'),
});

/**
 * Une question que Colette pose pour pouvoir faire mieux.
 *
 * A distinguer de `uncertainties`, qui est un JOURNAL de doutes destine a la trace : une
 * question, elle, appelle une reponse et sert a relancer la comprehension. « Il manque
 * l'heure d'arrivee » est une incertitude ; « A quelle heure dois-tu etre a Paris ? » est
 * une question.
 *
 * Les suggestions evitent d'avoir a taper : sur un telephone, en marchant, choisir entre
 * quatre reponses est la seule interaction realiste.
 */
export const questionSchema = z.object({
  question: z.string().min(1).max(200),
  suggestions: z.array(z.string().min(1).max(60)).max(4).default([]),
});

/**
 * A qui la chose est destinee, quand le message le dit : « pour moi » -> prive,
 * « surprise pour papa avec maman » -> choisi [maman] — la personne a surprendre n'y
 * figure JAMAIS. Dans le doute, foyer : c'est le defaut de la spec des cercles.
 */
export const audienceSuggereeSchema = z.object({
  type: z.enum(['foyer', 'prive', 'choisi']),
  prenoms: z.array(z.string().min(1).max(60)).max(8).default([]),
});

/**
 * Le garde-fou ABSURDE, pas la limite d'usage.
 *
 * Les plafonds serres (20) faisaient echouer la comprehension ENTIERE des qu'une semaine
 * complete etait dictee : `too_big`, et le message passait en « Echec » sans qu'une seule
 * proposition ne survive (constate trois fois en production les 27 et 28 aout 2026, sur
 * le planning hebdomadaire d'une testeuse).
 *
 * Le vrai plafond d'usage vit desormais dans `limiterFiche` (@colette/core), qui TAILLE
 * et le DIT, au lieu de tout jeter. Celui-ci ne protege plus que du modele parti en
 * vrille — un cas qui n'est jamais arrive.
 */
export const PLAFOND_ABSURDE = 200;

/**
 * Meme raisonnement pour la LONGUEUR des textes, et il a fallu une epreuve reelle pour
 * s'en apercevoir (2026-08-29) : une seule incertitude de 400 caracteres faisait
 * rejeter la fiche ENTIERE (`too_big`, maximum 300). Un appel facture, zero resultat.
 *
 * Le plafond d'usage (300 / 500) vit desormais dans `limiterFiche`, qui RACCOURCIT et
 * marque la coupe. Celui-ci ne protege plus que d'un modele parti en vrille.
 */
export const LONGUEUR_ABSURDE = 4000;

/**
 * Un fait que Colette PROPOSE de retenir (etape B2, 2026-09-10).
 *
 * B1 a donne a Colette une memoire qu'on remplit a la main. B2 la laisse proposer ce
 * qu'elle vient d'apprendre (« Adam sort a 16h30 le mardi ») — comme un rendez-vous, le
 * fait n'existe qu'une fois VALIDE : rien n'entre dans la memoire sans un geste.
 *
 * `sujet` est un PRENOM (celui du foyer), pas un identifiant : le modele ne voit que des
 * prenoms. La traduction en personne se fait a la validation, cote serveur.
 *
 * Aucun `min(1)` et seulement le plafond ABSURDE : un sujet vide ou un texte de 400
 * caracteres ne doivent pas faire rejeter la fiche ENTIERE (piege paye le 2026-08-29 —
 * un appel facture, zero resultat). `limiterFiche` ecarte les vides et raccourcit.
 */
export const faitPayloadSchema = z.object({
  sujet: z.string().max(LONGUEUR_ABSURDE).nullable(),
  texte: z.string().max(LONGUEUR_ABSURDE),
});

export const ficheComprehensionSchema = z.object({
  summary: z.string().min(1).max(LONGUEUR_ABSURDE),
  events: z.array(eventPayloadSchema).max(PLAFOND_ABSURDE),
  tasks: z.array(taskPayloadSchema).max(PLAFOND_ABSURDE),
  reminders: z.array(reminderPayloadSchema).max(PLAFOND_ABSURDE),
  checklists: z.array(checklistPayloadSchema).max(PLAFOND_ABSURDE),
  uncertainties: z.array(z.string().max(LONGUEUR_ABSURDE)).max(PLAFOND_ABSURDE),
  // `default([])` : les fiches produites avant l'ajout des questions restent valides, et
  // un modele qui omettrait le champ ne fait pas echouer toute la comprehension.
  questions: z.array(questionSchema).max(4).default([]),
  // `default(foyer)` : meme robustesse — une fiche sans audience est une fiche au foyer.
  audience: audienceSuggereeSchema.default({ type: 'foyer', prenoms: [] }),
  // `default([])` : les fiches d'avant l'etape B2 restent valides, et un modele qui
  // omet le champ ne fait pas echouer la comprehension. Plafond d'usage : limiterFiche.
  faits: z.array(faitPayloadSchema).max(PLAFOND_ABSURDE).default([]),
});

export const passwordChangeSchema = z.object({
  currentPassword: z.string().min(1, 'Le mot de passe actuel est obligatoire'),
  newPassword: passwordSchema,
});

export type ChecklistKind = z.infer<typeof checklistKindSchema>;
export type EventPayload = z.infer<typeof eventPayloadSchema>;
export type TaskPayload = z.infer<typeof taskPayloadSchema>;
export type ReminderPayload = z.infer<typeof reminderPayloadSchema>;
export type ChecklistPayload = z.infer<typeof checklistPayloadSchema>;
export type FaitPayload = z.infer<typeof faitPayloadSchema>;
export type Question = z.infer<typeof questionSchema>;
export type AudienceSuggeree = z.infer<typeof audienceSuggereeSchema>;
export type FicheComprehension = z.infer<typeof ficheComprehensionSchema>;
export type PasswordChangeInput = z.infer<typeof passwordChangeSchema>;
